import { StarIcon } from "@heroicons/react/16/solid";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import { useRef, useState } from "react";
import {
  FieldValues,
  RegisterOptions,
  UseFormReturn,
  useFormContext,
} from "react-hook-form";
import ButtonBase from "../Buttons/ButtonBase";
import { inputUtility } from "@repo/utilities/input.utility.ts";

type TTextInputProps = {
  containerClass?: string;
  label?: string;
  icon?: React.ReactNode;
  registerOptions?: RegisterOptions<FieldValues, string>;
  name: string;
  className?: string;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  textHint?: string;
  textHintClassName?: string;
  min?: string;
  max?: string;
  autoComplete?: string;
  allowFloat?: boolean;
  disableCopy?: boolean;
  disablePaste?: boolean;
};

function TextInput({
  containerClass,
  label,
  icon,
  registerOptions,
  name,
  className,
  placeholder,
  disabled,
  textHint,
  type = "text",
  min,
  max,
  autoComplete = "off",
  textHintClassName,
  allowFloat = false,
  disableCopy,
  disablePaste,
}: TTextInputProps) {
  const {
    register,
    setError,
    clearErrors,
    formState: { errors },
  }: UseFormReturn<FieldValues> = useFormContext();
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  let errorMessage = String(errors[name]?.message || "");

  if (String(name).includes(".")) {
    const [parent, index, child] = String(name).split(".");
    errorMessage = String(
      (errors as any)?.[parent]?.[index]?.[child]?.message || ""
    );
  }
  const handlePasswordText = () => {
    setPasswordVisible(!isPasswordVisible);
  };
  const registeredField = register(name, registerOptions);
  const hasRightIcon = icon || type === "password";

  const handleCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!disableCopy) return;
    e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!disablePaste) return;
    e.preventDefault();

    setError(name, {
      type: "manual",
      message: `Pasting is not allowed, please type your ${label?.toLowerCase()}`,
    });

    e.currentTarget.addEventListener("input", () => clearErrors(name), {
      once: true,
    });
  };
  return (
    <div className={`flex flex-col w-full ${containerClass}`}>
      {label && (
        <label className="text-xs flex items-center gap-1 text-left text-body font-semibold pl-1">
          {label}
          {registerOptions?.required && (
            <StarIcon className="size-2 text-error -translate-y-1" />
          )}
        </label>
      )}
      <section className="w-full relative">
        <input
          type={
            type === "password"
              ? !isPasswordVisible
                ? "password"
                : "text"
              : type
          }
          placeholder={placeholder}
          disabled={disabled}
          {...registeredField}
          ref={(element) => {
            inputRef.current = element;
            registeredField.ref(element);
          }}
          autoComplete={autoComplete}
          data-iserror={errorMessage ? true : false}
          className={`inputGroup h-10 w-full text-sm 
              text-body font-semibold pl-2 py-1 placeholder:font-medium placeholder:text-darkGray/80 outline-none rounded-lg
               z-10 border-2  ${
                 hasRightIcon ? "pr-10" : "pr-2"
               } border-extraLightGray focus:border-primary/50 disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-extraLightGray/30 
                ${className}`}
          onChange={(e) => {
            inputUtility?.stripHtmlTagsFromInput(e); // your global validation
            registeredField.onChange(e); // RHF default
            registerOptions?.onChange?.(e); // parent onChange
          }}
          min={min}
          max={max}
          onCopy={handleCopy}
          onPaste={handlePaste}
          step={allowFloat ? "any" : ""}
        />
        <button
          type="button"
          tabIndex={-1}
          data-error={errorMessage ? true : false}
          onMouseDown={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className="text-gray data-[error=true]:text-error/60 absolute w-5 right-4 top-1/2 -translate-y-1/2"
        >
          {icon}
          {type === "password" && (
            <ButtonBase
              type="button"
              buttonType="tertiary"
              icon={
                isPasswordVisible ? (
                  <EyeIcon className="size-4 text-gray" />
                ) : (
                  <EyeSlashIcon className="size-4 text-gray" />
                )
              }
              onClick={handlePasswordText}
            />
          )}
        </button>
      </section>

      {errorMessage ? (
        <p className="text-xs text-error/90 font-medium pl-1 ">
          {errorMessage}
        </p>
      ) : (
        textHint && (
          <p
            className={`text-xs font-medium text-gray pl-1 w-full ${textHintClassName}`}
          >
            {textHint}
          </p>
        )
      )}
    </div>
  );
}

export default TextInput;
